import styled from '@emotion/styled';
import { Icon } from '@mdi/react';
import { BaseButton, BaseNavLink, Variables } from '../../base';
import { TitleCard } from '../../card';
import { styledTitleCardSectionTitle } from '../claim-section.styles';

export const StyledTitleCard = styled(TitleCard)`
    ${styledTitleCardSectionTitle}
    padding-bottom: 24px;
    gap: 14px;
`;

export const ViewDetailsLink = styled(BaseNavLink)`
    font-size: 14px;
    font-weight: 500;
    color: ${Variables.ThemeLight.PrimaryConstant6};
    text-decoration: none;
    white-space: nowrap;

    &:hover {
        text-decoration: underline;
    }
`;

export const ViewDetailsButton = styled(BaseButton)`
    border: none;
    background: none;
    padding: 0;
    font-size: 14px;
    font-weight: 500;
    color: ${Variables.ThemeLight.PrimaryConstant6};
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        text-decoration: underline;
    }
`;

export const Content = styled.div`
    display: flex;
    margin: 0 24px;
    overflow-x: auto;
`;

export const Section = styled.div<{ $first?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 9px;
    min-width: 0;
    flex: 1 1 0;
    padding: ${props => (props.$first ? '0 20px 0 0' : '0 20px')};
    border-left: ${props => (props.$first ? 'none' : `1px solid ${Variables.ThemeLight.Border2}`)};
`;

export const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-height: 24px;
`;

export const SectionMenuIcon = styled(Icon)`
    width: 20px;
    height: 20px;
    fill: var(--text-mild, ${Variables.ThemeLight.TextMild});
`;

export const SectionMenuTrigger = styled(BaseButton)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: none;
    padding: 0;
    cursor: pointer;
    line-height: 0;

    &:hover ${SectionMenuIcon} {
        fill: var(--text-medium, ${Variables.ThemeLight.TextMedium});
    }
`;

export const SectionLabel = styled.div`
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${Variables.ThemeLight.TextXMild};
`;

export const NameRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    min-width: 0;
    flex-direction: column;
`;

export const Name = styled.div`
    font-size: 18px;
    font-weight: 600;
    line-height: 1.2;
    color: ${Variables.ThemeLight.PrimaryConstant6};
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const Subtitle = styled.div`
    font-size: 14px;
    line-height: 1.3;
    color: ${Variables.ThemeLight.TextMild};
`;

export const Demographics = styled.div`
    font-size: 14px;
    line-height: 1.3;
    color: ${Variables.ThemeLight.TextMild};
`;

export const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
`;

export const FieldLabel = styled.div`
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${Variables.ThemeLight.TextXMild};
`;

export const FieldValue = styled.div<{ $emphasized?: boolean }>`
    font-size: 14px;
    line-height: 1.35;
    color: ${Variables.ThemeLight.TextMedium};
    font-weight: ${props => (props.$emphasized ? 500 : 400)};
    word-break: break-word;
`;

export const EmptyValue = styled.div`
    font-size: 14px;
    line-height: 1.35;
    color: ${Variables.ThemeLight.TextXMild};
    font-style: italic;
`;

export const RoleBadge = styled.span<{ $variant: 'primary' | 'secondary' | 'tertiary' | 'guarantor' }>`
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    line-height: 1.2;

    ${props => {
        switch (props.$variant) {
            case 'primary':
                return `
                    color: ${Variables.ThemeLight.PrimaryOpacity10};
                    background-color: ${Variables.ThemeLight.PrimaryMild2};
                    border: 1px solid ${Variables.ThemeLight.PrimaryMild1};
                `;
            case 'secondary':
                return `
                    color: ${Variables.ThemeLight.TextMedium};
                    background-color: ${Variables.ThemeLight.NeutralMild3};
                    border: 1px solid ${Variables.ThemeLight.NeutralMild1};
                `;
            case 'tertiary':
                return `
                    color: ${Variables.Colors.rose20};
                    background-color:${Variables.Colors.rose5};
                    border: 1px solid ${Variables.Colors.rose15};
                `;
            case 'guarantor':
            default:
                return `
                    color: ${Variables.ThemeLight.TextMedium};
                    background-color: ${Variables.Colors.white};
                    border: 1px solid ${Variables.ThemeLight.Border2};
                `;
        }
    }}
`;

export const MakePaymentButton = styled(BaseButton)`
    flex-shrink: 0;
    height: 32px;
    padding: 0 10px;
    font-size: 13px;
    white-space: nowrap;
    border-radius: 6px;
`;

export const LockIcon = styled(Icon)`
    width: 14px;
    height: 14px;
    fill: currentColor;
    margin-right: 4px;
`;

export const ChevronIcon = styled(Icon)`
    width: 14px;
    height: 14px;
    fill: currentColor;
    vertical-align: middle;
`;

export const Loader = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 160px;
    margin: 0 24px;

    & > svg {
        background-color: transparent;
    }
`;
